import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, ExternalLink, Building2, ArrowUp, ArrowDown, DoorOpen } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import StatusBadge from "@/components/StatusBadge";
import PropertyFormModal from "@/components/PropertyFormModal";
import ImageUpload from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUSES = ["Available", "Pending Lease", "Rented"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [licences, setLicences] = useState(null);
  const [licencesSaving, setLicencesSaving] = useState(false);
  const [expandedRooms, setExpandedRooms] = useState({});
  const [creds, setCreds] = useState({ current: "", email: "", password: "" });
  const [credsSaving, setCredsSaving] = useState(false);

  const refresh = () => {
    setLoading(true);
    api.get("/properties").then((r) => setProperties(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user) {
      refresh();
      api.get("/site-settings").then((r) => setLicences(r.data.licences || [])).catch(() => setLicences([]));
    }
  }, [user]);

  if (user === null) {
    return <div className="min-h-screen bg-[#EFF0F2] dark:bg-[#12100E]" />;
  }
  if (user === false) {
    return <Navigate to="/admin/login" replace />;
  }

  const available = properties.filter((p) => p.status === "Available").length;
  const rented = properties.filter((p) => p.status === "Rented").length;
  const occupancy = properties.length ? Math.round((rented / properties.length) * 100) : 0;
  const roomsAvailable = properties.reduce(
    (n, p) => n + (p.rooms || []).filter((r) => r.status === "Available").length,
    0
  );

  const confirmDelete = async () => {
    try {
      await api.delete(`/properties/${deleting.id}`);
      toast.success(`"${deleting.title}" deleted`);
      setDeleting(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const changeStatus = async (p, status) => {
    try {
      const { id, created_at, ...body } = p;
      await api.put(`/properties/${p.id}`, { ...body, status });
      toast.success(`"${p.title}" marked as ${status}`);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const changeRoomStatus = async (p, roomId, status) => {
    try {
      const { id, created_at, ...body } = p;
      const rooms = (p.rooms || []).map((r) => (r.id === roomId ? { ...r, status } : r));
      await api.put(`/properties/${p.id}`, { ...body, rooms });
      toast.success(`Room marked as ${status}`);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= properties.length) return;
    const next = [...properties];
    [next[index], next[target]] = [next[target], next[index]];
    setProperties(next);
    try {
      await api.post("/properties/reorder", { ids: next.map((p) => p.id) });
      toast.success("Order saved");
    } catch (err) {
      toast.error(formatApiError(err));
      refresh();
    }
  };

  const setLicence = (i, key, value) =>
    setLicences((ls) => ls.map((l, idx) => (idx === i ? { ...l, [key]: value } : l)));

  const addLicence = () =>
    setLicences((ls) => [...ls, { id: Date.now(), name: "", text: "", logo: "" }]);

  const removeLicence = (i) =>
    setLicences((ls) => ls.filter((_, idx) => idx !== i));

  const saveCreds = async () => {
    setCredsSaving(true);
    try {
      await api.post("/auth/change-credentials", {
        current_password: creds.current,
        new_email: creds.email || null,
        new_password: creds.password || null,
      });
      toast.success("Login details updated — please sign in again");
      await logout();
      navigate("/admin/login");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCredsSaving(false);
    }
  };

  const saveLicences = async () => {
    setLicencesSaving(true);
    try {
      await api.put("/site-settings", { licences });
      toast.success("Licences saved — live on the site now");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLicencesSaving(false);
    }
  };

  const stats = [
    { label: "Total Units", value: properties.length },
    { label: "Available Now", value: available },
    { label: "Occupancy Rate", value: `${occupancy}%` },
    { label: "Rooms Available", value: roomsAvailable },
  ];

  return (
    <div className="min-h-screen bg-[#EFF0F2] dark:bg-[#12100E]">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-mono text-[#A05A32] dark:text-[#D9773F]">Owner Dashboard</p>
            <h1 data-testid="admin-dashboard-title" className="mt-1 text-3xl sm:text-4xl font-serif font-light text-[#1A1715] dark:text-[#F7F4F0]">
              Your Portfolio
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#E4DBD0] dark:border-[#352F2B] px-4 py-2.5 text-sm text-[#57524E] dark:text-[#C7BEB5] hover:bg-[#F4EFEA] dark:hover:bg-[#231F1C] transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" /> View Public Site
            </Link>
            <Button
              data-testid="admin-add-property-btn"
              onClick={() => { setEditing(null); setModalOpen(true); }}
              className="rounded-full bg-[#A05A32] hover:bg-[#864522] text-[#EFF0F2]"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Add Property
            </Button>
            <button
              data-testid="admin-logout-btn"
              onClick={async () => { await logout(); navigate("/admin/login"); }}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#E4DBD0] dark:border-[#352F2B] px-4 py-2.5 text-sm text-[#57524E] dark:text-[#C7BEB5] hover:bg-[#F4EFEA] dark:hover:bg-[#231F1C] transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s) => (
            <div key={s.label} className="rounded-2xl border border-[#E4DBD0] dark:border-[#352F2B] bg-white dark:bg-[#1A1715] p-5">
              <p className="font-serif text-3xl text-[#1A1715] dark:text-[#F7F4F0]">{s.value}</p>
              <p className="mt-1 text-xs uppercase tracking-wider text-[#8A837C] dark:text-[#8E867E]">{s.label}</p>
            </div>
          ))}
        </div>

        <p className="mt-8 text-xs text-[#8A837C] dark:text-[#8E867E]">
          Use the arrows to change the order properties appear on the site. Status is set automatically from room availability.
        </p>
        <div className="mt-3 space-y-4" data-testid="admin-property-list">
          {loading ? (
            <div className="h-24 rounded-2xl bg-[#ECE5DD] dark:bg-[#231F1C] animate-pulse" />
          ) : properties.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#E4DBD0] dark:border-[#352F2B] py-20 text-center">
              <Building2 className="mx-auto h-8 w-8 text-[#8A837C]" />
              <p className="mt-3 font-serif text-2xl text-[#1A1715] dark:text-[#F7F4F0]">No properties yet</p>
              <p className="text-sm text-[#8A837C] dark:text-[#8E867E]">Click "Add Property" to publish your first listing.</p>
            </div>
          ) : (
            properties.map((p, index) => (
              <div
                key={p.id}
                data-testid={`admin-property-row-${p.id}`}
                className="rounded-2xl border border-[#E4DBD0] dark:border-[#352F2B] bg-white dark:bg-[#1A1715] p-4 sm:p-5 flex flex-col md:flex-row md:items-center gap-4 flex-wrap"
              >
                <div className="flex md:flex-col gap-1 shrink-0">
                  <button
                    data-testid={`admin-property-up-${p.id}`}
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    aria-label="Move up"
                    className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-[#E4DBD0] dark:border-[#352F2B] text-[#57524E] dark:text-[#C7BEB5] hover:bg-[#F4EFEA] dark:hover:bg-[#231F1C] transition-colors disabled:opacity-30"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={`admin-property-down-${p.id}`}
                    onClick={() => move(index, 1)}
                    disabled={index === properties.length - 1}
                    aria-label="Move down"
                    className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-[#E4DBD0] dark:border-[#352F2B] text-[#57524E] dark:text-[#C7BEB5] hover:bg-[#F4EFEA] dark:hover:bg-[#231F1C] transition-colors disabled:opacity-30"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </button>
                </div>
                {p.image ? (
                  <img src={p.image} alt={p.title} className="h-20 w-full md:w-32 rounded-xl object-cover shrink-0" />
                ) : (
                  <div className="h-20 w-full md:w-32 rounded-xl bg-[#ECE5DD] dark:bg-[#231F1C] shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-serif text-xl text-[#1A1715] dark:text-[#F7F4F0] truncate">{p.title}</p>
                    <StatusBadge status={p.status} testId={`admin-property-status-badge-${p.id}`} />
                  </div>
                  <p className="mt-0.5 text-sm text-[#8A837C] dark:text-[#8E867E] truncate">
                    {p.type} · {p.location} · {p.rooms?.length ? `${p.rooms.length} rooms` : `${p.bedrooms} bd / ${p.bathrooms} ba`}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                  {p.rooms?.length > 0 && (
                    <button
                      data-testid={`admin-property-rooms-toggle-${p.id}`}
                      onClick={() => setExpandedRooms((e) => ({ ...e, [p.id]: !e[p.id] }))}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#E4DBD0] dark:border-[#352F2B] px-4 py-2.5 text-sm text-[#57524E] dark:text-[#C7BEB5] hover:bg-[#F4EFEA] dark:hover:bg-[#231F1C] transition-colors"
                    >
                      <DoorOpen className="h-3.5 w-3.5" /> Rooms ({p.rooms.length}) {expandedRooms[p.id] ? "▲" : "▼"}
                    </button>
                  )}
                  {(!p.rooms || p.rooms.length === 0) && (
                    <Select value={p.status} onValueChange={(v) => changeStatus(p, v)}>
                      <SelectTrigger data-testid={`admin-property-status-toggle-${p.id}`} className="w-[150px] h-10 rounded-full bg-white dark:bg-[#1A1715] border-[#E4DBD0] dark:border-[#352F2B]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <button
                    data-testid={`admin-property-edit-btn-${p.id}`}
                    onClick={() => { setEditing(p); setModalOpen(true); }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#E4DBD0] dark:border-[#352F2B] px-4 py-2.5 text-sm text-[#57524E] dark:text-[#C7BEB5] hover:bg-[#F4EFEA] dark:hover:bg-[#231F1C] transition-colors"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button
                    data-testid={`admin-property-delete-btn-${p.id}`}
                    onClick={() => setDeleting(p)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#DFC7BA] px-4 py-2.5 text-sm text-[#874D2E] hover:bg-[#F5EFEB] dark:border-[#4F2618] dark:text-[#FCA5A5] dark:hover:bg-[#2A1911] transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>
                {expandedRooms[p.id] && p.rooms?.length > 0 && (
                  <div
                    data-testid={`admin-property-rooms-${p.id}`}
                    className="w-full md:basis-full border-t border-[#EDE6DE] dark:border-[#28231F] pt-3 flex flex-wrap gap-2"
                  >
                    {p.rooms.map((r) => (
                      <div
                        key={r.id}
                        data-testid={`admin-room-chip-${r.id}`}
                        className="inline-flex items-center gap-2 rounded-full border border-[#E4DBD0] dark:border-[#352F2B] bg-[#FAF8F5] dark:bg-[#231F1C] pl-4 pr-1.5 py-1.5"
                      >
                        <span className="text-sm text-[#1A1715] dark:text-[#F7F4F0]">
                          {r.title} <span className="text-[#8A837C] dark:text-[#8E867E]">· £{Number(r.rent).toLocaleString()}</span>
                        </span>
                        <Select value={r.status} onValueChange={(v) => changeRoomStatus(p, r.id, v)}>
                          <SelectTrigger
                            data-testid={`admin-room-status-${r.id}`}
                            className="h-7 w-[135px] rounded-full text-xs bg-white dark:bg-[#1A1715] border-[#E4DBD0] dark:border-[#352F2B]"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {licences && (
          <section className="mt-12" data-testid="admin-licences-section">
            <h2 className="font-serif text-2xl sm:text-3xl text-[#1A1715] dark:text-[#F7F4F0]">Licences & Accreditations</h2>
            <p className="mt-1 text-sm text-[#8A837C] dark:text-[#8E867E]">
              These four slots show at the bottom of your public contact section. Add a name, a short line, and upload a logo.
            </p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {licences.map((l, i) => (
                <div
                  key={i}
                  data-testid={`admin-licence-${i}`}
                  className="rounded-2xl border border-[#E4DBD0] dark:border-[#352F2B] bg-white dark:bg-[#1A1715] p-4 space-y-2.5"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-xs uppercase tracking-wider text-[#8A837C] dark:text-[#8E867E]">Licence {i + 1}</p>
                    <button
                      type="button"
                      data-testid={`admin-licence-remove-${i}`}
                      onClick={() => removeLicence(i)}
                      className="text-xs text-[#874D2E] dark:text-[#FCA5A5] hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                  <Input
                    data-testid={`admin-licence-name-${i}`}
                    value={l.name || ""}
                    onChange={(e) => setLicence(i, "name", e.target.value)}
                    placeholder={`Licence ${i + 1} name (e.g. NRLA Member)`}
                  />
                  <Input
                    data-testid={`admin-licence-text-${i}`}
                    value={l.text || ""}
                    onChange={(e) => setLicence(i, "text", e.target.value)}
                    placeholder="Short line (e.g. Membership no. 12345)"
                  />
                  <ImageUpload
                    compact
                    testId={`admin-licence-logo-${i}`}
                    value={l.logo || ""}
                    onChange={(v) => setLicence(i, "logo", v)}
                  />
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-3">
              <Button
                data-testid="admin-licences-save-btn"
                onClick={saveLicences}
                disabled={licencesSaving}
                className="rounded-full bg-[#A05A32] hover:bg-[#864522] text-[#EFF0F2]"
              >
                {licencesSaving ? "Saving..." : "Save licences"}
              </Button>
              <Button
                type="button"
                variant="outline"
                data-testid="admin-licences-add-btn"
                onClick={addLicence}
                className="rounded-full"
              >
                + Add licence
              </Button>
            </div>
          </section>
        )}

        <section className="mt-12" data-testid="admin-security-section">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#1A1715] dark:text-[#F7F4F0]">Login & Security</h2>
          <p className="mt-1 text-sm text-[#8A837C] dark:text-[#8E867E]">
            Change the email and/or password you use to sign in. You'll be asked to sign in again afterwards.
          </p>
          <div className="mt-4 max-w-md rounded-2xl border border-[#E4DBD0] dark:border-[#352F2B] bg-white dark:bg-[#1A1715] p-5 space-y-3">
            <Input
              data-testid="admin-security-current"
              type="password"
              value={creds.current}
              onChange={(e) => setCreds((c) => ({ ...c, current: e.target.value }))}
              placeholder="Current password (required)"
            />
            <Input
              data-testid="admin-security-email"
              type="email"
              value={creds.email}
              onChange={(e) => setCreds((c) => ({ ...c, email: e.target.value }))}
              placeholder={`New email (current: ${user.email})`}
            />
            <Input
              data-testid="admin-security-password"
              type="password"
              value={creds.password}
              onChange={(e) => setCreds((c) => ({ ...c, password: e.target.value }))}
              placeholder="New password (min 6 characters)"
            />
            <Button
              data-testid="admin-security-save-btn"
              onClick={saveCreds}
              disabled={credsSaving || !creds.current || (!creds.email && !creds.password)}
              className="rounded-full bg-[#A05A32] hover:bg-[#864522] text-[#EFF0F2]"
            >
              {credsSaving ? "Updating..." : "Update login"}
            </Button>
          </div>
        </section>
      </main>

      <PropertyFormModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        property={editing}
        onSaved={refresh}
      />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete this listing?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleting?.title}" will be permanently removed from your public portfolio. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel-btn">Keep listing</AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-delete-confirm-btn"
              onClick={confirmDelete}
              className="bg-[#874D2E] hover:bg-[#6d3d23] text-white"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
